import type {
  AeropayBankAccount,
  AeropayUser,
  BankAccountResponse,
  CreateAeropayUser,
  LinkBankAccountProps,
} from '@iheartjane/data-access';
import type {
  BankLinkingEvent,
  BankLinkingJaneUser,
  MoreInfoFormData,
} from '@iheartjane/menu';
import {
  AeropayScreen,
  AlreadyEnrolledScreen,
  DeleteBankAccountScreen,
  FailureScreen,
  JaneGoldPromptScreen,
  LinkBankScreen,
  MoreInfoScreen,
  NewAccountLinkedScreen,
  SCREEN_NAMES,
  SuccessScreen,
  WIDGET_CONTAINER_ID,
  WelcomeBackScreen,
  getMfaContactDisplay,
  useAerosync,
  useBankLinkingModalData,
  useMfa,
} from '@iheartjane/menu';
import { Box, Loading, Modal } from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import type { User } from '@iheartjane/types';
import type { AerosyncWidget } from 'aerosync-web-sdk';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router';

import { useAeropayBankConnection } from '@jane/bloom/hooks/bankLinking/useAeropayBankConnection';
import { useAeropayUser } from '@jane/bloom/hooks/bankLinking/useAeropayUser';
import { useFetcherWithMutation } from '@jane/bloom/hooks/useFetcherWithMutation';
import type { BloomCalloutVariants } from '@jane/bloom/services/tracking/Events';
import { track } from '@jane/bloom/services/tracking/mixpanel';
import type { RootLoaderData } from '@jane/bloom/types/schemas/bloom/rootLoader';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';

import styles from './BloomBankLinkingModal.module.css';

export interface BloomBankLinkingModalProps {
  /** opened to add an additional bank account */
  addNewAccount?: boolean;
  aeropayConsumerId: string;
  aeropayEnv: string;
  /** Bank account selected for deletion */
  deleteBankAccount?: AeropayBankAccount | null;
  /** Flag used for displaying a different message in the Delete Bank Account screen */
  isLastBankAccount?: boolean;
  onRequestClose(): void;
  open: boolean;
  source: BloomCalloutVariants;
}

export const BloomBankLinkingModal = ({
  addNewAccount = false,
  aeropayConsumerId,
  aeropayEnv,
  deleteBankAccount,
  isLastBankAccount = false,
  onRequestClose,
  open,
  source,
}: BloomBankLinkingModalProps) => {
  const [error, setError] = useState<unknown>(null);

  // root loader: optional in tests / stories
  const rootData = useRouteLoaderData<RootLoaderData>('root');
  const whoamiUser = rootData?.whoami?.user;
  const janeUser: BankLinkingJaneUser | null = isJaneUser(whoamiUser)
    ? { user: whoamiUser }
    : null;

  const { getNewPhone, requiresMfa } = useMfa();
  const usesEmail = false; // Bloom defaults to SMS; no useEmailOverSMS config available in Bloom
  const contactDisplay = getMfaContactDisplay({
    newPhone: getNewPhone(),
    user: isJaneUser(whoamiUser) ? whoamiUser : null,
    usesEmail,
  });

  const user: MoreInfoFormData | undefined = isJaneUser(whoamiUser)
    ? {
        firstName: whoamiUser.first_name ?? undefined,
        lastName: whoamiUser.last_name ?? undefined,
        phone: whoamiUser.phone ?? undefined,
      }
    : undefined;

  const {
    data: bankConnectionData,
    fetchBankConnection,
    state: bankConnectionState,
  } = useAeropayBankConnection();

  const { data: aeropayUserData, fetchAeropayUser } = useAeropayUser();

  const aeropayUser: AeropayUser | null | undefined =
    aeropayUserData?.success === true ? aeropayUserData.payload : undefined;
  const aeropayUserError =
    aeropayUserData?.success === false ? aeropayUserData.errors : undefined;

  // load aeropay user on open; fetchAeropayUser omitted from deps — unstable identity loops the effect
  useEffect(() => {
    if (!open) return;
    if (aeropayUserData == null) fetchAeropayUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aeropayUserData]);

  // fetch widget token only once an aeropay account exists — bank-connection 400s without one
  useEffect(() => {
    if (!open || !aeropayUser?.user_id) return;
    if (bankConnectionData == null) fetchBankConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aeropayUser?.user_id, bankConnectionData]);

  // mutation adapters for useBankLinkingModalData
  const { mutate: updateJaneUser } = useFetcherWithMutation<
    Partial<User>,
    unknown
  >({
    action: `/api/update-account`,
    toFormData: (vars) => ({
      birthdate: vars.birth_date ?? null,
      firstName: vars.first_name ?? null,
      lastName: vars.last_name ?? null,
      phoneNumber: vars.phone ?? null,
    }),
  });

  const { mutate: createAeropayUserMutate } = useFetcherWithMutation<
    CreateAeropayUser,
    AeropayUser
  >({
    action: `/api/aeropay-user`,
    toFormData: (vars) => ({
      email: vars.email ?? null,
      firstName: vars.first_name ?? null,
      lastName: vars.last_name ?? null,
      phone: vars.phone ?? null,
    }),
  });

  // fetch widget token after mid-flow account creation
  const createAeropayUser: typeof createAeropayUserMutate = (vars, options) =>
    createAeropayUserMutate(vars, {
      ...options,
      onSuccess: (created) => {
        fetchBankConnection();
        options?.onSuccess?.(created);
      },
    });

  const { mutate: linkAeropayBankAccount } = useFetcherWithMutation<
    LinkBankAccountProps,
    BankAccountResponse
  >({
    action: `/api/bank-account`,
    toFormData: (vars) => ({
      aeropayUserId: vars.aeropayUserId,
      aeropayUserPassword: vars.aeropayUserPassword,
    }),
  });

  const { mutate: deleteAeropayBankAccount } = useFetcherWithMutation<
    { bankAccountId: string | undefined },
    BankAccountResponse | null
  >({
    action: `/api/bank-account/delete`,
    toFormData: (vars) => ({ bankAccountId: vars.bankAccountId ?? null }),
  });

  const { mutate: selectAeropayBankAccount } = useFetcherWithMutation<
    { bankAccountId: string | undefined },
    BankAccountResponse
  >({
    action: `/api/bank-account/select`,
    toFormData: (vars) => ({ bankAccountId: vars.bankAccountId ?? null }),
  });

  const { mutate: confirmJaneGoldUser } = useFetcherWithMutation<void, unknown>(
    {
      action: `/api/aeropay-user/confirm-gold`,
      toFormData: () => ({}),
    }
  );

  const handleTrackEvent = useCallback(
    (event: BankLinkingEvent) => {
      switch (event.kind) {
        case 'unlinkedLastBankAccount':
          track({
            email: event.email,
            event: EventNames.UnlinkedLastBankAccount,
            username: event.username,
          });
          return;
        case 'linkedFirstBankAccount':
          track({
            email: event.email,
            event: EventNames.LinkedFirstBankAccount,
            isJanePay: event.isJanePay,
            source,
            username: event.username,
          });
          return;
        case 'linkedNewBankAccount':
          track({
            email: event.email,
            event: EventNames.LinkedNewBankAccount,
            isJanePay: event.isJanePay,
            source,
            username: event.username,
          });
          return;
        case 'linkBankAccountFailed':
          track({
            email: event.email,
            event: EventNames.LinkBankAccountFailed,
            username: event.username,
          });
          return;
      }
    },
    [source]
  );

  const {
    deletingBankAccount,
    handleCloseWithOptOut,
    handleDeleteBankAccount,
    handleSubmitInfo,
    screen,
    setScreen,
  } = useBankLinkingModalData({
    addNewAccount,
    aeropayUser,
    aeropayUserError,
    deleteBankAccount: deleteBankAccount ?? null,
    isJanePay: false,
    janeUser,
    onConfirmJaneGoldUser: () => confirmJaneGoldUser(undefined),
    onCreateAeropayUser: createAeropayUser,
    onDeleteAeropayBankAccount: deleteAeropayBankAccount,
    onRequestClose,
    onSelectAeropayBankAccount: selectAeropayBankAccount,
    onTrackEvent: handleTrackEvent,
    onUpdateJaneUser: updateJaneUser,
    setError,
    showPrompt: false,
  });

  const handleError = useCallback(
    (err: unknown) => {
      setError(err);
      setScreen(SCREEN_NAMES.error);
    },
    [setScreen]
  );

  // surface bank-connection load failures into the state machine
  useEffect(() => {
    if (
      bankConnectionState === 'idle' &&
      bankConnectionData?.success === false
    ) {
      handleError(bankConnectionData.errors);
    }
  }, [bankConnectionState, bankConnectionData, handleError]);

  const bankConnectionPayload =
    bankConnectionData?.success === true ? bankConnectionData.payload : null;

  const {
    isLoading: isLoadingWidget,
    linkingBankAccount,
    widgetRef,
  } = useAerosync({
    aeropayConsumerId,
    aeropayEnv,
    onError: handleError,
    onLinkBankAccount: linkAeropayBankAccount,
    onSuccess: () =>
      setScreen(
        addNewAccount ? SCREEN_NAMES.newAccountSuccess : SCREEN_NAMES.success
      ),
    token: bankConnectionPayload?.token,
    widgetContainerId: WIDGET_CONTAINER_ID,
  });

  const renderSwitch = () => {
    switch (screen) {
      case SCREEN_NAMES.loading:
        return (
          <Box py={48} position="relative">
            <Loading color="black" size="lg" />
          </Box>
        );
      case SCREEN_NAMES.welcomeBack:
        return (
          <WelcomeBackScreen onDoneClick={() => handleCloseWithOptOut()} />
        );
      case SCREEN_NAMES.alreadyEnrolled:
        return (
          <AlreadyEnrolledScreen
            hideJanePayBranding={false}
            isJanePay={false}
            onDoneClick={onRequestClose}
          />
        );
      case SCREEN_NAMES.linkBank:
        return (
          <LinkBankScreen
            hideJanePayBranding={false}
            isJanePay={false}
            onContinueClick={() => setScreen(SCREEN_NAMES.aeropay)}
            onDoneClick={onRequestClose}
            shouldShowGold={true}
          />
        );
      case SCREEN_NAMES.moreInfo:
        return (
          <MoreInfoScreen
            onDoneClick={onRequestClose}
            onSubmit={handleSubmitInfo}
            user={user}
          />
        );
      case SCREEN_NAMES.moreInfoAeropay:
        return (
          <MoreInfoScreen
            onDoneClick={onRequestClose}
            onSubmit={handleSubmitInfo}
            user={user}
          />
        );
      case SCREEN_NAMES.promptLinkBank:
        return (
          <JaneGoldPromptScreen
            hideJanePayBranding={false}
            onContinue={() => setScreen(SCREEN_NAMES.aeropay)}
            onRequestClose={onRequestClose}
          />
        );
      case SCREEN_NAMES.deleteBankAccountScreen:
        return (
          <DeleteBankAccountScreen
            isRemoving={deletingBankAccount}
            lastBankAccount={isLastBankAccount}
            onCancelClick={onRequestClose}
            onRemoveClick={handleDeleteBankAccount}
          />
        );
      case SCREEN_NAMES.aeropay:
        return (
          <AeropayScreen
            contactDisplay={contactDisplay}
            isLinkingBankAccount={linkingBankAccount}
            isLoadingWidget={isLoadingWidget}
            onCancel={onRequestClose}
            requiresMfa={requiresMfa()}
            usesEmail={usesEmail}
            widgetRef={widgetRef as MutableRefObject<AerosyncWidget | null>}
          />
        );
      case SCREEN_NAMES.success:
        return (
          <SuccessScreen
            hideJanePayBranding={false}
            isJanePay={false}
            isMarketplace={false}
            onDoneClick={() => handleCloseWithOptOut()}
          />
        );
      case SCREEN_NAMES.newAccountSuccess:
        return (
          <NewAccountLinkedScreen onDoneClick={() => handleCloseWithOptOut()} />
        );
      case SCREEN_NAMES.error:
        return (
          <FailureScreen
            error={error}
            onDoneClick={onRequestClose}
            onTryAgainClick={() => {
              setError(null);
              setScreen(SCREEN_NAMES.linkBank);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      variant="dialogue"
      open={open}
      onRequestClose={onRequestClose}
      overlayClose
      overlayClassName={styles['overlay']}
    >
      <Modal.Content>
        <Box maxWidth={screen === SCREEN_NAMES.aeropay ? 'auto' : '279px'}>
          {renderSwitch()}
        </Box>
      </Modal.Content>
    </Modal>
  );
};
